import { useState } from "react";
import { toast } from "react-toastify";
import { useNavigate, Navigate } from "react-router-dom";
import Calling from "../../api/Calling"; // Replace with actual API calling function
import { TextField, Button, InputAdornment, IconButton, CircularProgress } from "@mui/material";
import { Lock, Eye, EyeOff, KeyRound } from "lucide-react";
import AuthShell, { darkFieldSx, authButtonSx } from "./AuthShell";

export default function NewPw() {
    const [loader, setLoader] = useState(false);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [showConfirmPw, setShowConfirmPw] = useState(false);
    const [errors, setErrors] = useState({});
    const navigate = useNavigate();

    // 🔒 Block direct access — must come from forgot-password + verified OTP steps
    if (!localStorage.getItem('forgetPw') || !localStorage.getItem('forgetOtpVerified')) {
        return <Navigate to="/admin/panel/forgetpw" replace />;
    }

    const validate = () => {
        const e = {};
        if (!password) e.password = "Password is required";
        else if (password.length < 6) e.password = "Password must be at least 6 characters";
        if (!confirmPassword) e.confirmPassword = "Please confirm your password";
        else if (password !== confirmPassword) e.confirmPassword = "Passwords do not match";
        return e;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const v = validate();
        if (Object.keys(v).length) {
            setErrors(v);
            return;
        }
        setErrors({});
        setLoader(true);

        const body = {
            password: password,
        };

        try {
            const resp = await Calling.ResetPassword(body); // Replace with actual API call for password reset
            const { status, message } = resp;
            if (status) {
                toast.success(message, {
                    autoClose: 2000,
                });
                setTimeout(() => {
                    localStorage.removeItem('forgetPw');
                    localStorage.removeItem('forgetOtpVerified');
                    navigate("/admin/panel/login"); // Replace with the actual route after password reset
                }, 2000);
            } else {
                toast.error(message, {
                    autoClose: 2000,
                });
            }
        } catch (error) {
            navigate('/Error');
        } finally {
            setLoader(false);
        }
    };

    return (
        <>
            <title>New Password</title>

            <AuthShell
                icon={<KeyRound size={30} />}
                title="Set New Password"
                subtitle="Create a new password for your account"
            >
                <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
                    <TextField
                        label="New Password"
                        type={showPw ? "text" : "password"}
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); if (errors.password) setErrors((p) => ({ ...p, password: "" })); }}
                        required
                        fullWidth
                        error={!!errors.password}
                        helperText={errors.password}
                        sx={darkFieldSx}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock size={18} color="rgba(255,255,255,0.55)" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPw((s) => !s)}
                                            edge="end"
                                            aria-label="toggle password visibility"
                                            sx={{ color: "rgba(255,255,255,0.8)", '&:hover': { color: '#34C759' } }}
                                        >
                                            {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                    <TextField
                        label="Confirm Password"
                        type={showConfirmPw ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => { setConfirmPassword(e.target.value); if (errors.confirmPassword) setErrors((p) => ({ ...p, confirmPassword: "" })); }}
                        required
                        fullWidth
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword}
                        sx={darkFieldSx}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock size={18} color="rgba(255,255,255,0.55)" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowConfirmPw((s) => !s)}
                                            edge="end"
                                            aria-label="toggle confirm password visibility"
                                            sx={{ color: "rgba(255,255,255,0.8)", '&:hover': { color: '#34C759' } }}
                                        >
                                            {showConfirmPw ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disableElevation
                        disabled={loader}
                        sx={authButtonSx}
                        startIcon={loader ? <CircularProgress size={18} color="inherit" /> : <KeyRound size={18} />}
                    >
                        {loader ? "Resetting..." : "Reset Password"}
                    </Button>
                </form>
            </AuthShell>
        </>
    );
}
